/**
  * useNavTabs
  *
  * Encapsulates all navigation-tab state and handlers:
  *   - Opening / focusing tabs
  *   - Closing tabs with MRU-aware fallback selection
  *   - Recent-tab history (with pin/unpin support)
  *   - Collapsed group tracking
  *   - Agent-route → tab auto-open side-effect
  *   - Workspace secondary-item tab open (for DockFlyout integration)
  *
  * AppShell stays a pure layout container and delegates every tab
  * mutation to this hook.
  */

import { useCallback, useEffect, useRef, useState, type MutableRefObject, type SetStateAction } from "react";
import {
  type ChatTabContext,
  type NavTab,
  type NavTabContext,
  type RecentTabEntry,
  type TabAddAction,
  type RecentTabActionId,
  type RecentTabActionPayload,
  type RecentTabActionResult,
  NAV_FOCUS_TAB_ID,
} from "kci-design-lib";
import { useNavigation, buildWorkspaceContainerToken } from "../stores/useAppStore";
import { useT } from "../i18n/I18nProvider";
import { CONFIG } from "../constants";
import { parseAgentNewTaskPageId } from "../services/routes";

import type { UseDockFlyoutOptions } from "./useDockFlyout";
import { kciEventBus } from "../eventBus/KCiEventBus";
import { KCI_APP_IDS } from "../eventBus/appIds";
import { dispatchActivityDashboardLoad, dispatchChatSessionLoad } from "../utils";
import {
  initTabPanel,
  fetchRecentTabs,
  pinTab,
  unpinTab,
  saveTab,
  saveTabs,
  removeTab,
  renameSession,
  deleteSession,
  downloadChat,
  getAgentDetails,
  shareAgent,
  deleteAgent,
  deleteAgentRuns,
  buildRecentTabIdentifierMap,
  mapBackendTabsToRecentEntries,
  getUserSessions,
  type BackendRecentTab,
  type RecentTabIdentifiers,
  type SaveTabPayload,
  type TabPin,
} from "../services/tabManagementService";
import { fetchWorkspaceObjectives } from "../services/dockFlyoutDataService";
import { toBackendTabContext } from "../services/tabContextAdapter";
import { selectActiveTab, selectActiveTabContext, selectTabs } from "../stores/tabSelectors";
import { useTabStore } from "../stores/useTabStore";
import { type UserChatSessionDTO } from "../services/chatHistoryApi";

// Event bus type definition
interface KCiEventBusSubscriber {
  subscribe: (type: string, handler: (event: { type: string; payload?: Record<string, unknown> }) => void) => () => void;
}

interface ChatSessionCreatedDetail {
  sessionId?: string;
  name?: string;
  agentId?: string;
  agentName?: string;
  isGlobal?: boolean;
  isCustomAgent?: boolean;
  isAgentPreview?: boolean;
}

// Constants
const MRU_HISTORY_SIZE = 5;
const RECENT_TABS_LIMIT = 10;
const MAX_RECENT_TAB_TITLE_LENGTH = 120;

const createChatTabContext = (
  sessionId: string | null,
  title: string,
): ChatTabContext => ({ kind: "chat", sessionId, title });

export const AGENT_DASHBOARD_TAB_ID = "agent:all-agents-activity";
const AGENT_DASHBOARD_AGENT_ID = "AG-0000";

// Tab ids that host the Agent Builder form (Angular MFE) — used by the shell
// to guard tab switches/closes when that form has unsaved changes.
export const AGENT_BUILDER_TAB_ID = "agent:agent-builder";
export const AGENT_NEW_AGENT_TAB_ID = "agent:new-agent";

const AGENT_BUILDER_PAGE_IDS = new Set([
  "agent-builder",
  "agents-builder",
  "agent-builder-home",
  "builder",
  "build-new-agent",
]);

const AGENT_NEW_PAGE_IDS = new Set(["new-agent", "create-agent"]);

const AGENT_BUILDER_LABEL = "Agent Builder";
export const AGENT_DASHBOARD_LABEL =
  "All Agents Activity";
const DELETED_CHAT_SESSION_IDS_STORAGE_KEY = "kci.deletedChatSessionIds";

// Tracks which objective workspace tabs are currently open, keyed as
// "objective:<objectiveId>" -> tabId. Kept in sync with the identically
// named key BodyContent.tsx reads to decide whether a hidden objective
// container may be pruned when its owning tab is gone.
const TAB_ROUTE_ID_STORAGE_KEY = "kci-core.navTabs.routeToId.v1";

// Pure helpers (no React dependency)
function toTitleCaseFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function toCanonicalAgentKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function sanitizeTitle(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}


function sanitizeDownloadFileName(value: string): string {
  const normalized = value
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized || "Chat";
}

function resolveBackendTabType(tab: NavTab): SaveTabPayload["Type"] {
  if (tab.tabKind === "workspaces") return "Workspace";
  if (tab.tabKind === "agents") return "Agent";
  return "Chat";
}

function toPersistedSaveTabPayload(
  tab: NavTab,
  identifierMap: Map<string, RecentTabIdentifiers>,
  isClosed = false,
): SaveTabPayload | null {
  if (tab.id === NAV_FOCUS_TAB_ID) {
    return null;
  }

  const normalizedLabel = sanitizeTitle(tab.label || "");
  const identifiers = identifierMap.get(tab.id);
  const sessionId = identifiers?.sessionId
    ?? (tab.id.startsWith("chat:") ? tab.id.slice("chat:".length).trim() : null)
    ?? undefined;

  return {
    TabId: tab.id,
    Title: normalizedLabel || "Untitled",
    Type: resolveBackendTabType(tab),
    SessionId: sessionId || undefined,
    AgentId: tab.agentId ?? identifiers?.agentId ?? undefined,
    AgentName: tab.agentName ?? undefined,
    IsGlobal: tab.agentId ? tab.isGlobalAgent === true : undefined,
    IsClosed: isClosed,
    ...toBackendTabContext(tab.context),
  };
}

function isRecentEntryForSession(
  item: RecentTabEntry,
  renamedEntryId: string,
  sessionId: string,
  identifierMap: Map<string, RecentTabIdentifiers>,
): boolean {
  if (item.id === renamedEntryId || item.id === sessionId || item.id === `chat:${sessionId}`) {
    return true;
  }

  const itemIdentifiers = identifierMap.get(item.id);
  return itemIdentifiers?.sessionId === sessionId;
}

function isNavTabForSession(
  tab: NavTab,
  tabId: string,
  sessionId: string,
  identifierMap: Map<string, RecentTabIdentifiers>,
): boolean {
  if (
    tab.id === tabId ||
    tab.id === sessionId ||
    tab.id === `chat:${sessionId}`
  ) {
    return true;
  }

  const tabIdentifiers = identifierMap.get(tab.id);

  return tabIdentifiers?.sessionId === sessionId;
}

function resolveAgentTabIdFromEntry(entry: RecentTabEntry): string {
  if (entry.id.startsWith("agent:")) {
    return entry.id;
  }

  const labelKey = toCanonicalAgentKey(
    (entry.label || "").replace(/\s+activity$/i, "").trim(),
  );

  return labelKey ? `agent:${labelKey}` : entry.id;
}

function resolveOpenTabIdForEntry(entry: RecentTabEntry, tabs: NavTab[]): string | null {
  const directMatch = tabs.find((tab) => tab.id === entry.id);
  if (directMatch) {
    return directMatch.id;
  }

  if (entry.tabKind !== "agents") {
    return null;
  }

  const canonicalId = resolveAgentTabIdFromEntry(entry);
  if (canonicalId) {
    const canonicalMatch = tabs.find((tab) => tab.id === canonicalId);
    if (canonicalMatch) {
      return canonicalMatch.id;
    }
  }

  const normalizedLabel = sanitizeTitle(entry.label || "").toLowerCase();
  if (!normalizedLabel) {
    return null;
  }

  const labelMatch = tabs.find(
    (tab) =>
      tab.tabKind === "agents" && sanitizeTitle(tab.label || "").toLowerCase() === normalizedLabel,
  );

  return labelMatch?.id ?? null;
}

function normalizeActionError(
  action: "rename" | "share" | "download" | "edit" | "delete",
  error: unknown,
): string {
  if (error instanceof Error && error.message?.trim()) {
    return error.message.trim();
  }

  if (action === "rename") return "Unable to rename this session right now. Please try again.";
  if (action === "share") return "Unable to share this agent right now. Please try again.";
  if (action === "download") return "Unable to download this session right now. Please try again.";
  if (action === "edit") return "Unable to update this agent right now. Please try again.";
  return "Unable to delete this item right now. Please try again.";
}

function readStoredDeletedChatSessionIds(): Set<string> {
  if (typeof window === "undefined") {
    return new Set<string>();
  }

  try {
    const raw = window.localStorage.getItem(DELETED_CHAT_SESSION_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as unknown : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.map((id) => String(id).trim()).filter(Boolean)
        : [],
    );
  } catch (error) {
    console.warn('[KCI-ChatDelete][RecentTabs] failed to read stored deleted chat ids', error);
    return new Set<string>();
  }
}

function writeStoredDeletedChatSessionIds(sessionIds: Set<string>): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      DELETED_CHAT_SESSION_IDS_STORAGE_KEY,
      JSON.stringify([...sessionIds]),
    );
  } catch (error) {
    console.warn('[KCI-ChatDelete][RecentTabs] failed to store deleted chat ids', error);
  }
}

function readObjectiveTabRouteMap(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(TAB_ROUTE_ID_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch (error) {
    console.warn('[KCI-NavTabs] failed to read objective tab route map', error);
    return {};
  }
}

function writeObjectiveTabRouteMap(map: Record<string, string>): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(TAB_ROUTE_ID_STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    console.warn('[KCI-NavTabs] failed to store objective tab route map', error);
  }
}

// Marks an objective workspace tab as open so BodyContent's keep-alive
// stack knows not to prune its hidden MFE container.
function markObjectiveTabOpen(objectiveId: string, tabId: string): void {
  const map = readObjectiveTabRouteMap();
  map[`objective:${objectiveId}`] = tabId;
  writeObjectiveTabRouteMap(map);
}

// Marks an objective workspace tab as closed so BodyContent's keep-alive
// stack can prune its hidden MFE container instead of reusing stale state.
function markObjectiveTabClosed(objectiveId: string): void {
  const map = readObjectiveTabRouteMap();
  delete map[`objective:${objectiveId}`];
  writeObjectiveTabRouteMap(map);
}

// An objective workspace tab's subPage can be set from either its
// context.objectiveId (handleWorkspaceSecondaryTabOpen, on first open) or
// its tab.id suffix after "::" (activateNavTab, on reactivation) — see the
// two navigateToPage call sites. Both are candidates for the workspace
// container token this tab's hidden MFE container may have been keyed on.
function getWorkspaceObjectiveCandidates(tab: NavTab): string[] {
  const candidates = new Set<string>();
  if (tab.context?.kind === "workspace" && tab.context.objectiveId) {
    candidates.add(tab.context.objectiveId);
  }
  const separatorIndex = tab.id.indexOf("::");
  candidates.add(separatorIndex >= 0 ? tab.id.slice(separatorIndex + 2) : tab.id);
  return [...candidates];
}

function resolveAgentIdFromPageId(pageId: string): string | undefined {
  const match = /^agent-(.+)$/i.exec(pageId.trim());
  return match?.[1]?.toUpperCase() || undefined;
}

function resolveAgentTabDescriptor(
  pageId: string
): { id: string; label: string; agentId?: string } {
  const normalizedPageId = pageId.toLowerCase();

  if (
    normalizedPageId === CONFIG.FIXED_TAB_HOME ||
    normalizedPageId === CONFIG.PAGE_ACTION_TRAY ||
    normalizedPageId === "agent-ag-0000" ||
    normalizedPageId === "dashboard"
  ) {
    return {
      id: AGENT_DASHBOARD_TAB_ID,
      label: AGENT_DASHBOARD_LABEL,
      agentId: AGENT_DASHBOARD_AGENT_ID,
    };
  }

  const baseAgentPageId =
    parseAgentNewTaskPageId(normalizedPageId) ?? normalizedPageId;

  if (
    AGENT_BUILDER_PAGE_IDS.has(normalizedPageId) ||
    AGENT_BUILDER_PAGE_IDS.has(baseAgentPageId)
  ) {
    return { id: "agent:agent-builder", label: AGENT_BUILDER_LABEL };
  }

  if (
    AGENT_NEW_PAGE_IDS.has(normalizedPageId) ||
    AGENT_NEW_PAGE_IDS.has(baseAgentPageId)
  ) {
    return { id: "agent:agent-builder", label: AGENT_BUILDER_LABEL };
  }

  const label = toTitleCaseFromSlug(baseAgentPageId);

  // Canonicalize by agent identity so different route ids for the same
  // agent (e.g. rebate-analyzer vs agent-AG-1234) focus the same tab.
  const dedupeBasis = label;
  const canonicalId = `agent:${toCanonicalAgentKey(
    dedupeBasis.replace(/\s+activity$/i, "").trim()
  )}`;

  return {
    id: canonicalId,
    label,
    agentId: resolveAgentIdFromPageId(baseAgentPageId),
  };
}

function resolveAgentPageFromTabId(tabId: string): string {
  const normalizedTabId = tabId.startsWith("agent:")
    ? tabId.slice("agent:".length)
    : tabId;

  if (normalizedTabId === "all-agents-activity") {
    return "dashboard";
  }

  if (normalizedTabId === "agent-builder") {
    return "agent-builder";
  }

  if (normalizedTabId === "new-agent") {
    return "new-agent";
  }

  return normalizedTabId;
}

function isAgentNavigationPage(currentApp: string, currentPage: string): boolean {
  if (currentApp === CONFIG.AGENTS) {
    return true;
  }

  if (currentApp !== CONFIG.CLARA_APP) {
    return false;
  }

  const normalizedPageId = currentPage.toLowerCase();
  
  // __home__ is shared by workspace navigation in CLARA_APP, so treating it
  // as an agent route causes unintended agent-tab creation during workspace flows.
  // Keep dashboard/action-tray as explicit agent dashboard markers.
  if (
    normalizedPageId === CONFIG.PAGE_ACTION_TRAY ||
    normalizedPageId === "dashboard"
  ) {
    return true;
  }

  const baseAgentPageId =
    parseAgentNewTaskPageId(normalizedPageId) ?? normalizedPageId;

  if (
    AGENT_BUILDER_PAGE_IDS.has(normalizedPageId) ||
    AGENT_BUILDER_PAGE_IDS.has(baseAgentPageId)
  ) {
    return true;
  }

  if (
    AGENT_NEW_PAGE_IDS.has(normalizedPageId) ||
    AGENT_NEW_PAGE_IDS.has(baseAgentPageId)
  ) {
    return true;
  }

  if (normalizedPageId.startsWith("agent-")) {
    return true;
  }

  return false;
}

// Public interface
// A single, generic descriptor for any tab-changing action that must be
// deferred while the Agent Builder form (Angular MFE) is unsaved — every
// entry point (activate, close, open/focus, add-action) funnels through
// this instead of each call site inventing its own pending-action shape.
export type PendingAgentBuilderTabAction =
  | { kind: "activate"; tabId: string; options?: { trackFocus?: boolean; skipChatSessionLoad?: boolean } }
  | { kind: "close"; tabId: string }
  | { kind: "open"; nextTab: NavTab }
  | { kind: "add-action"; action: TabAddAction }
  | { kind: "navigation"; execute: () => void }
  | { kind: "dock-navigation"; execute: () => void };

export interface UseNavTabsResult {
  navTabs: NavTab[];
  activeNavTabId: string;
  activeNavTab: NavTab | null;
  activeNavTabContext: NavTabContext | null;
  recentNavTabs: RecentTabEntry[];
  collapsedGroupIds: string[];
  activateNavTab: (
    tabId: string,
    options?: { trackFocus?: boolean; skipChatSessionLoad?: boolean },
    activatedTab?: NavTab,
  ) => void;
  handleNavTabClose: (tabId: string) => void;
  handleNavAddAction: (action: TabAddAction) => void;
  handleRecentNavTabOpen: (entry: RecentTabEntry) => void;
  handleRecentNavTabPinToggle: (
    entry: RecentTabEntry,
    shouldPin: boolean
  ) => void;
  handleRecentNavTabAction: (
    entry: RecentTabEntry,
    actionId: RecentTabActionId,
    payload?: RecentTabActionPayload,
  ) => Promise<RecentTabActionResult>;
  handleWorkspaceSecondaryTabOpen: NonNullable<
    UseDockFlyoutOptions["onWorkspaceSecondaryItemSelect"]
  >;
  handleChatItemTabOpen: NonNullable<
    UseDockFlyoutOptions["onChatItemSelect"]
  >;
  handleAgentItemTabOpen: NonNullable<
    UseDockFlyoutOptions["onAgentItemSelect"]
  >;
  handleDashboardNewChatAction: NonNullable<
    UseDockFlyoutOptions["onNewChatActionSelect"]
  >;
  handleDashboardNewAgentAction: NonNullable<
    UseDockFlyoutOptions["onNewAgentActionSelect"]
  >;
  onToggleGroupCollapse: (groupId: string) => void;
  openOrFocusNavTab: (tab: NavTab) => void;
  handleOpenReviewBalanceTab: (storageKey: string, datasetLabel: string) => void;
  handleOpenInTab: (detail: { contentType: string; storageKey: string; tabLabel: string }) => void;
  isAgentBuilderDirtyRef: MutableRefObject<boolean>;
  // Generic unsaved-changes guard state, shared by every tab-changing entry point.
  pendingAgentBuilderNavAction: PendingAgentBuilderTabAction | null;
  confirmAgentBuilderNavAction: () => void;
  cancelAgentBuilderNavAction: () => void;
  isAgentBuilderNavGuardActive: (targetTabId?: string) => boolean;
  queuePendingAgentBuilderAction: (pending: PendingAgentBuilderTabAction) => void;
}


// Hook
export function useNavTabs(): UseNavTabsResult {
  const t = useT();
  const { navigation, navigateToPage, removeWorkspaceContainerToken } = useNavigation();
  const { state: tabState, dispatch: tabDispatch } = useTabStore();
  const navTabs = selectTabs(tabState);
  const activeNavTabId = tabState.activeTabId;
  const activeNavTab = selectActiveTab(tabState);
  const activeNavTabContext = selectActiveTabContext(tabState);
  const recentNavTabs = [...tabState.recentTabs];
  const collapsedGroupIds = [...tabState.collapsedGroupIds];

  // Refs kept in sync with state so callbacks never close over stale values.
  const navTabsRef = useRef<NavTab[]>(navTabs);
  const recentNavTabsRef = useRef<RecentTabEntry[]>(recentNavTabs);
  const navMruRef = useRef<string[]>([]);
  const activeNavTabIdRef = useRef<string>(NAV_FOCUS_TAB_ID);
  const previousActiveNavTabIdRef = useRef<string | null>(null);
  const navCounterRef = useRef(0);
  const suppressNextAgentAutoTabRef = useRef(false);
  const translateRef = useRef(t);
  const createdChatSessionIdsRef = useRef<Set<string>>(new Set());
  const chatTitleBySessionIdRef = useRef<Map<string, string>>(new Map());
  const renamedChatTitlesRef = useRef<Map<string, string>>(new Map());
  const deletedChatSessionIdsRef = useRef<Set<string>>(readStoredDeletedChatSessionIds());

  // Backend tab panel refs (stable, no re-render on change).
  const tabPanelIdRef = useRef<string | null>(null);
  const tabPanelPinsRef = useRef<TabPin[]>([]);
  const recentTabIdentifiersRef = useRef<Map<string, RecentTabIdentifiers>>(new Map());
  const pendingAgentTabSaveRef = useRef<SaveTabPayload | null>(null);

  const setNavTabs = useCallback((value: SetStateAction<NavTab[]>) => {
    const next = typeof value === "function" ? value(navTabsRef.current) : value;
    navTabsRef.current = next;
    tabDispatch({ type: "REPLACE_TABS", payload: { tabs: next } });
  }, [tabDispatch]);

  const setActiveNavTabId = useCallback((tabId: string) => {
    activeNavTabIdRef.current = tabId;
    tabDispatch({ type: "SET_ACTIVE_TAB", payload: { tabId } });
  }, [tabDispatch]);

  const setRecentNavTabs = useCallback((value: SetStateAction<RecentTabEntry[]>) => {
    const next = typeof value === "function" ? value(recentNavTabsRef.current) : value;
    recentNavTabsRef.current = next;
    tabDispatch({ type: "REPLACE_RECENT", payload: { entries: next } });
  }, [tabDispatch]);

  const isBackendRecentTabForDeletedChat = useCallback((tab: BackendRecentTab) => {
    const deletedSessionIds = deletedChatSessionIdsRef.current;
    if (deletedSessionIds.size === 0) {
      return false;
    }

    const tabSessionId = (tab.SessionId ?? '').trim();
    const tabId = (tab.TabId ?? '').trim();

    if (tabSessionId && deletedSessionIds.has(tabSessionId)) return true;
    if (tabId && deletedSessionIds.has(tabId)) return true;
    if (tabId.startsWith('chat:') && deletedSessionIds.has(tabId.slice('chat:'.length))) return true;

    return false;
  }, []);

  const getResolvedChatLabel = (tab: NavTab): string => {
    if (tab.tabKind !== "chats") {
      return tab.label;
    }
     const identifiers = recentTabIdentifiersRef.current.get(tab.id);
     const sessionId =
       identifiers?.sessionId ??
       (tab.id.startsWith("chat:")
         ? tab.id.slice("chat:".length)
         : tab.id);
    return renamedChatTitlesRef.current.get(sessionId) ?? tab.label;
  };

  // Keep refs in sync with state.
  useEffect(() => {
    navTabsRef.current = navTabs;
  }, [navTabs]);

  useEffect(() => {
    recentNavTabsRef.current = recentNavTabs;
  }, [recentNavTabs]);

  useEffect(() => {
    activeNavTabIdRef.current = activeNavTabId;
  }, [activeNavTabId]);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  const preserveLocalAgentDashboardEntry = useCallback((entries: RecentTabEntry[]) => {
    if (entries.some((entry) => entry.id === AGENT_DASHBOARD_TAB_ID)) {
      return entries;
    }

    const localDashboardEntry = recentNavTabsRef.current.find(
      (entry) => entry.id === AGENT_DASHBOARD_TAB_ID,
    );

    return localDashboardEntry
      ? [localDashboardEntry, ...entries].slice(0, RECENT_TABS_LIMIT)
      : entries;
  }, []);

  // IMPORTANT: Legacy recent-tab persistence/retrieval contract.
  // Keep this flow: panel id -> fetchRecentTabs -> build identifiers ->
  // map backend tabs -> setRecentNavTabs. New functionality may filter/augment
  // the backend result, but must not replace this retrieval pipeline.
  const refreshRecentTabsFromBackend = useCallback(async () => {
    const panelId = tabPanelIdRef.current;
    if (!panelId) return;
    try {
      const backendTabs = await fetchRecentTabs(panelId, 0, RECENT_TABS_LIMIT);
      const filteredBackendTabs = backendTabs.filter((tab) => !isBackendRecentTabForDeletedChat(tab));
      const filteredCount = backendTabs.length - filteredBackendTabs.length;
      console.info('[KCI-ChatDelete][RecentTabs] refresh from backend', {
        panelId,
        backendCount: backendTabs.length,
        agentTabs: backendTabs
          .filter((tab) => tab.Type === 'Agent')
          .map((tab) => ({ tabId: tab.TabId, agentId: tab.AgentId, title: tab.Title })),
        filteredDeletedCount: filteredCount,
        deletedSessionIds: [...deletedChatSessionIdsRef.current],
        filteredTabs: backendTabs
          .filter(isBackendRecentTabForDeletedChat)
          .map((tab) => ({ tabId: tab.TabId, sessionId: tab.SessionId, title: tab.Title })),
      });
      recentTabIdentifiersRef.current = buildRecentTabIdentifierMap(filteredBackendTabs);
      const entries = mapBackendTabsToRecentEntries(filteredBackendTabs, tabPanelPinsRef.current);
      setRecentNavTabs(preserveLocalAgentDashboardEntry(entries));
    } catch (error) {
      console.warn('[KCI-ChatDelete][RecentTabs] backend refresh failed', error);
      // Silently fall back — keep last known state, tab bar stays functional.
    }
  }, [isBackendRecentTabForDeletedChat, preserveLocalAgentDashboardEntry, setRecentNavTabs]);

  // IMPORTANT: Legacy recent-tab hydration contract.
  // Recent tabs are restored from the backend on mount using initTabPanel()
  // followed by fetchRecentTabs(), identifier-map construction and
  // mapBackendTabsToRecentEntries(). Keep this sequence intact.
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const panel = await initTabPanel();
        if (cancelled) return;
        tabPanelIdRef.current = panel.Id;
        tabPanelPinsRef.current = panel.Pins;

        const backendTabs = await fetchRecentTabs(panel.Id, 0, RECENT_TABS_LIMIT);
        if (cancelled) return;

        const filteredBackendTabs = backendTabs.filter((tab) => !isBackendRecentTabForDeletedChat(tab));
        console.info('[KCI-ChatDelete][RecentTabs] hydrate from backend', {
          panelId: panel.Id,
          backendCount: backendTabs.length,
          agentTabs: backendTabs
            .filter((tab) => tab.Type === 'Agent')
            .map((tab) => ({ tabId: tab.TabId, agentId: tab.AgentId, title: tab.Title })),
          filteredDeletedCount: backendTabs.length - filteredBackendTabs.length,
          deletedSessionIds: [...deletedChatSessionIdsRef.current],
          filteredTabs: backendTabs
            .filter(isBackendRecentTabForDeletedChat)
            .map((tab) => ({ tabId: tab.TabId, sessionId: tab.SessionId, title: tab.Title })),
        });
        recentTabIdentifiersRef.current = buildRecentTabIdentifierMap(filteredBackendTabs);
        const entries = mapBackendTabsToRecentEntries(filteredBackendTabs, panel.Pins);
        setRecentNavTabs(preserveLocalAgentDashboardEntry(entries));

        const pendingAgentTab = pendingAgentTabSaveRef.current;
        if (pendingAgentTab) {
          pendingAgentTabSaveRef.current = null;
          console.info('[KCI-RecentTabs][Agent] flushing queued tab after hydration', {
            panelId: panel.Id,
            tabId: pendingAgentTab.TabId,
            title: pendingAgentTab.Title,
          });
          try {
            await saveTab(panel.Id, pendingAgentTab, { activeTabId: pendingAgentTab.TabId });
            if (cancelled) return;
            console.info('[KCI-RecentTabs][Agent] queued tab persisted', {
              panelId: panel.Id,
              tabId: pendingAgentTab.TabId,
            });
            await refreshRecentTabsFromBackend();
          } catch (error) {
            pendingAgentTabSaveRef.current = pendingAgentTab;
            console.error('[KCI-RecentTabs][Agent] queued tab persistence failed', {
              panelId: panel.Id,
              tabId: pendingAgentTab.TabId,
              error,
            });
          }
        }
      } catch (error) {
        console.warn('[KCI-ChatDelete][RecentTabs] hydrate failed', error);
        // Silently fall back — recentNavTabs stays empty, tab bar still functional.
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [isBackendRecentTabForDeletedChat, preserveLocalAgentDashboardEntry, refreshRecentTabsFromBackend]);

  // Tracks whether the Agent Builder form (Angular MFE) has unsaved changes,
  // reported via a cross-MFE window event — used to guard tab switches away from it.
  const isAgentBuilderDirtyRef = useRef(false);
  useEffect(() => {
    const handleDirtyState = (event: Event) => {
      const detail = (event as CustomEvent<{ isSaved?: boolean; isDirty?: boolean }>).detail;
      isAgentBuilderDirtyRef.current = detail?.isSaved === true
        ? false
        : detail?.isDirty === true;
    };
    window.addEventListener("kci-agent-builder-form-state", handleDirtyState);
    return () => window.removeEventListener("kci-agent-builder-form-state", handleDirtyState);
  }, []);

  // Tabs whose wizard form (Angular MFE) has unsaved field edits, reported by
  // the same kind of cross-MFE window event as the Agent Builder form.
  const dirtyWizardTabIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handleWizardDirtyState = (event: Event) => {
      const detail = (event as CustomEvent<{ isDirty?: boolean; tabId?: string }>).detail;
      console.log('[KCI-Chat][NavTabs] handleWizardDirtyState', detail);
      const tabId = detail?.tabId?.trim() || activeNavTabIdRef.current;
      if (!tabId) return;

      if (detail?.isDirty === true) {
        dirtyWizardTabIdsRef.current.add(tabId);
      } else {
        dirtyWizardTabIdsRef.current.delete(tabId);
      }
    };
    window.addEventListener("kci-wizard-form-state", handleWizardDirtyState);
    return () => window.removeEventListener("kci-wizard-form-state", handleWizardDirtyState);
  }, []);

  // Single generic descriptor of whatever tab-changing action is currently
  // deferred while Agent Builder is unsaved — every guarded entry point
  // (activate/close/open/add-action) reads and resolves through this state.
  const [pendingAgentBuilderNavAction, setPendingAgentBuilderNavAction] =
    useState<PendingAgentBuilderTabAction | null>(null);

  // Pure predicate: true when the currently active tab has unsaved changes
  // (Agent Builder form or a wizard form) and targetTabId (if given) would
  // move away from it.
  const isAgentBuilderNavGuardActive = useCallback(
    (targetTabId?: string) => {
      const currentId = activeNavTabIdRef.current;
      if (targetTabId !== undefined && targetTabId === currentId) return false;

      const isAgentBuilderActive =
        currentId === AGENT_BUILDER_TAB_ID || currentId === AGENT_NEW_AGENT_TAB_ID;
      if (isAgentBuilderActive && isAgentBuilderDirtyRef.current) return true;

      return dirtyWizardTabIdsRef.current.has(currentId);
    },
    [],
  );

  const queuePendingAgentBuilderAction = useCallback(
    (pending: PendingAgentBuilderTabAction) => setPendingAgentBuilderNavAction(pending),
    [],
  );

  // activateNavTab
  const activateNavTab = useCallback(
    (
      tabId: string,
      options?: { trackFocus?: boolean, skipChatSessionLoad?: boolean },
      activatedTab?: NavTab,
    ) => {
      if (isAgentBuilderNavGuardActive(tabId)) {
        queuePendingAgentBuilderAction({ kind: "activate", tabId, options });
        return;
      }
      if (activeNavTabIdRef.current !== tabId) {
        previousActiveNavTabIdRef.current = activeNavTabIdRef.current;
        activeNavTabIdRef.current = tabId;
      }
      setActiveNavTabId(tabId);

      if (tabId === NAV_FOCUS_TAB_ID && !options?.trackFocus) {
        return;
      }

      const nextMru = [
        tabId,
        ...navMruRef.current.filter((id) => id !== tabId),
      ].slice(0, MRU_HISTORY_SIZE);
      navMruRef.current = nextMru;

      const tab = activatedTab
        ?? navTabsRef.current.find((item) => item.id === tabId);
      if (!tab) {
        return;
      }
      if (tab.id === "agent:agent-builder") {
        window.dispatchEvent(new CustomEvent("kci-agent-builder-tab-selected"));
        return;
      }

      console.info('[KCI-NavTabs] Activating tab context', {
        tabId: tab.id,
        tabKind: tab.tabKind ?? null,
        context: tab.context ?? null,
      });

      // NAV_FOCUS_TAB_ID is the blank "New Chat" tab in this shell, not the
      // main focus page. It must still route to the chat surface and clear any
      // previous session when activated.
      if (tab.id === NAV_FOCUS_TAB_ID && tab.tabKind !== "chats") {
        return;
      }

      const tabIdentifiers = recentTabIdentifiersRef.current.get(tab.id);

      const sessionIdentity =
        tabIdentifiers?.sessionId ??
        (tab.id.startsWith("chat:")
          ? tab.id.slice("chat:".length)
          : tab.id);

      if (tab.tabKind === "workspaces") {
        if (tab.context?.kind === "workspace") {
          kciEventBus.publish(
            "kci.workspace.itemSelected",
            {
              tabId: tab.id,
              workspaceId: tab.context.workspaceId,
              objectiveId: tab.context.objectiveId,
              objectiveType: tab.context.objectiveType ?? "",
              objectiveCategory: tab.context.objectiveCategory ?? "",
            },
            {
              source: KCI_APP_IDS.SHELL,
              target: KCI_APP_IDS.WORKSPACE_APP,
            },
          );
        }

        const workspaceHomeSubPage = tab.id.includes("::")
          ? tab.id.slice(tab.id.indexOf("::") + 2)
          : tab.id;

        // File-view tabs are always opened under CONFIG.CLARA_APP (see the
        // kci:workspace-file-view handler in AppShell.tsx) — reactivating
        // one must target the same app, otherwise it flips between
        // CLARA_APP and WORKSPACE across opens/switches and BodyContent
        // renders it from two different branches, forcing a remount.
        if (tab.id.startsWith("file-view:")) {
          if (
            navigation.currentApp !== CONFIG.CLARA_APP ||
            navigation.currentPage !== tab.id
          ) {
            navigateToPage(CONFIG.CLARA_APP, tab.id);
          }
          return;
        }

        // Review Balance tabs are workspace-kind but navigate to a dedicated page
        // rather than __home__, so the MFE renders ReviewBalancePage instead of WorkspacePage.
        if (tab.id.startsWith("review-balance:")) {
          const storageKey = tab.id.slice("review-balance:".length);
          if (
            navigation.currentApp !== CONFIG.WORKSPACE ||
            navigation.currentPage !== "__review-balance__" ||
            navigation.currentSubPage !== storageKey
          ) {
            navigateToPage(CONFIG.WORKSPACE, "__review-balance__", storageKey);
          }
        } else if (tab.id.startsWith("data-driven-insights:")) {
          const storageKey = tab.id.slice("data-driven-insights:".length);
          if (
            navigation.currentApp !== CONFIG.WORKSPACE ||
            navigation.currentPage !== "__data-driven-insights__" ||
            navigation.currentSubPage !== storageKey
          ) {
            navigateToPage(CONFIG.WORKSPACE, "__data-driven-insights__", storageKey);
          }
        } else if (tab.id.startsWith("data-driven-assessment:")) {
          const storageKey = tab.id.slice("data-driven-assessment:".length);
          if (
            navigation.currentApp !== CONFIG.WORKSPACE ||
            navigation.currentPage !== "__data-driven-assessment__" ||
            navigation.currentSubPage !== storageKey
          ) {
            navigateToPage(CONFIG.WORKSPACE, "__data-driven-assessment__", storageKey);
          }
        } else if (tab.id.startsWith("view-report:")) {
          const storageKey = tab.id.slice("view-report:".length);
          if (
            navigation.currentApp !== CONFIG.WORKSPACE ||
            navigation.currentPage !== "__view-report__" ||
            navigation.currentSubPage !== storageKey
          ) {
            navigateToPage(CONFIG.WORKSPACE, "__view-report__", storageKey);
          }
        } else if (tab.id === "execution-stream") {
          if (
            (navigation.currentApp !== CONFIG.WORKSPACE ||
              navigation.currentPage !== "__execution-stream__")
          ) {
            navigateToPage(CONFIG.WORKSPACE, "__execution-stream__");
          }
        } else if (
          navigation.currentApp !== CONFIG.WORKSPACE ||
          navigation.currentPage !== CONFIG.FIXED_TAB_HOME ||
          navigation.currentSubPage !== workspaceHomeSubPage
        ) {
          navigateToPage(CONFIG.WORKSPACE, CONFIG.FIXED_TAB_HOME, workspaceHomeSubPage);
        }
      } else if (tab.tabKind === "chats") {
        const sessionId = tab.context?.kind === "chat"
          ? tab.context.sessionId
          : tabIdentifiers?.sessionId
            ?? (tab.id.startsWith("chat:") ? tab.id.slice("chat:".length) : null);
        const fullscreenSessionId = tab.id.startsWith("ai-chat-")
          ? window.sessionStorage.getItem(`kci.fullscreenAiSession.${tab.id}`)
          : null;
        const sessionIdToLoad = fullscreenSessionId ?? sessionId;

        const isCurrentChatRoute =
          navigation.currentApp === CONFIG.CLARA_APP &&
          navigation.currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT &&
          navigation.currentSubPage === sessionIdToLoad;

        if (!options?.skipChatSessionLoad) {
          dispatchChatSessionLoad(sessionIdToLoad);
        }
        if (!isCurrentChatRoute) {
          navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_CLARA_AI_CHAT, sessionIdToLoad);
        }
      } else if (tab.tabKind === "agents") {
        if (tab.id === AGENT_DASHBOARD_TAB_ID) {
          dispatchActivityDashboardLoad();
        } else {
          const nextAgentPage = resolveAgentPageFromTabId(tab.id);
          if (
            navigation.currentApp !== CONFIG.AGENTS ||
            navigation.currentPage !== nextAgentPage
          ) {
            navigateToPage(CONFIG.AGENTS, nextAgentPage);
          }
        }
      }

      // IMPORTANT: Legacy recent-tab patching/MRU contract.
      // Every real tab activation must move the corresponding recent entry to
      // the front, preserve pin state, de-duplicate by session identity, and
      // cap the list at RECENT_TABS_LIMIT.
      // New metadata (renamed titles, agent-conversation flags, etc.) is layered
      // onto this legacy update rather than replacing it.
      // Filter out Focus from recent tabs
      if (tab.id === NAV_FOCUS_TAB_ID) {
        setRecentNavTabs((prev) =>
          prev.filter((item) => item.id !== NAV_FOCUS_TAB_ID),
        );
        return;
      }

      const resolvedLabel = getResolvedChatLabel(tab);

      // When switching back to a renamed chat, make the actual
      // navigation tab use the renamed title as well.
      if (
        tab.tabKind === "chats" &&
        tab.id.startsWith("chat:") &&
        tab.label !== resolvedLabel
      ) {
        const nextTabs = navTabsRef.current.map((item) =>
          item.id === tab.id
            ? {
                ...item,
                label: resolvedLabel,
                labelKey: undefined,
              }
            : item,
        );

        navTabsRef.current = nextTabs;
        setNavTabs(nextTabs);
      }

      const entry: RecentTabEntry = {
        ...createRecentTabEntryFromNavTab(tab),
        label: resolvedLabel,
      };

      setRecentNavTabs((prev) => {
        const existing = prev.find((item) =>
          isRecentEntryForSession(item, tab.id, sessionIdentity, recentTabIdentifiersRef.current),
        );
        const nextEntry: RecentTabEntry = {
          ...entry,
          isPinned: existing?.isPinned ?? false,
          pinnedAt: existing?.pinnedAt ?? null,
        };
        const filtered = prev.filter(
          (item) => !isRecentEntryForSession(item, tab.id, sessionIdentity, recentTabIdentifiersRef.current),
        );
        const next = [nextEntry, ...filtered].slice(
          0,
          RECENT_TABS_LIMIT,
        );
        recentNavTabsRef.current = next;
        return next;
      });

      if (tab.tabKind === "agents") {
        const panelId = tabPanelIdRef.current;
        const payload: SaveTabPayload = {
          TabId: tab.id,
          Title: resolvedLabel,
          Type: "Agent",
          AgentId:
            tab.agentId ??
            (tab.id === AGENT_DASHBOARD_TAB_ID ? AGENT_DASHBOARD_AGENT_ID : undefined),
          AgentName: tab.agentName ?? resolvedLabel,
          IsGlobal: tab.isGlobalAgent,
          ...toBackendTabContext(tab.context),
        };

        if (!panelId) {
          pendingAgentTabSaveRef.current = payload;
          console.info('[KCI-RecentTabs][Agent] queued tab while panel initializes', {
            tabId: tab.id,
            agentId: payload.AgentId,
            title: resolvedLabel,
          });
          return;
        }

        console.info('[KCI-RecentTabs][Agent] persisting activated tab', {
          panelId,
          tabId: tab.id,
          agentId: payload.AgentId,
          title: resolvedLabel,
        });
        void saveTab(panelId, payload, { activeTabId: tab.id })
          .then(() => {
            console.info('[KCI-RecentTabs][Agent] activated tab persisted', {
              panelId,
              tabId: tab.id,
            });
            return refreshRecentTabsFromBackend();
          })
          .catch((error) => {
            pendingAgentTabSaveRef.current = payload;
            console.error('[KCI-RecentTabs][Agent] activated tab persistence failed', {
              panelId,
              tabId: tab.id,
              error,
            });
          });
      }
    },
    [isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, navigateToPage, navigation.currentApp, navigation.currentPage, navigation.currentSubPage, refreshRecentTabsFromBackend, setActiveNavTabId, setNavTabs, setRecentNavTabs]
  );

  // openOrFocusNavTab (internal helper)
  const openOrFocusNavTab = useCallback(
    (nextTab: NavTab) => {
      if (isAgentBuilderNavGuardActive(nextTab.id)) {
        queuePendingAgentBuilderAction({ kind: "open", nextTab });
        return;
      }

      const contentType = nextTab.id.split(":")[0];
      const existing =
      navTabsRef.current.find((tab) => tab.id === nextTab.id) ??
      navTabsRef.current.find(
        (tab) =>
          nextTab.tabKind === "workspaces" &&
          !!nextTab.engId &&
          tab.tabKind === "workspaces" &&
          tab.engId === nextTab.engId &&
          tab.id.startsWith(`${contentType}:`),
      );

      if (existing) {
        if (existing.label !== nextTab.label || nextTab.context !== undefined) {
          setNavTabs((prev) => {
            const next = prev.map((tab) =>
              tab.id === existing.id
                ? {
                    ...tab,
                    ...nextTab,
                    id: existing.id,
                    context: nextTab.context ?? tab.context,
                  }
                : tab
            );
            navTabsRef.current = next;
            return next;
          });
        }
        activateNavTab(existing.id, { trackFocus: true });
        return;
      }
      const next = [...navTabsRef.current, nextTab];
      navTabsRef.current = next;
      setNavTabs(next);
      activateNavTab(nextTab.id, { trackFocus: true });
    },
    [activateNavTab, isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, setNavTabs]
  );

  // Apply freshly-fetched session titles onto open nav tabs + recent tabs.
  const applySessionTitles = useCallback(
    (sessions: UserChatSessionDTO[]) => {
      const titleBySessionId = new Map<string, string>();

      sessions.forEach((session) => {
        const title = sanitizeTitle(session.Name ?? "");
        const sessionId = session.SessionId?.trim();

        if (!title || !sessionId) return;

        titleBySessionId.set(sessionId, title);
        renamedChatTitlesRef.current.set(sessionId, title);
      });

      setNavTabs((tabs) =>
        tabs.map((tab) => {
          if (tab.tabKind !== "chats") {
            return tab;
          }

          const identifiers = recentTabIdentifiersRef.current.get(tab.id);
          const sessionId =
            identifiers?.sessionId ??
            (tab.id.startsWith("chat:") ? tab.id.slice("chat:".length) : null);

          if (!sessionId) {
            return tab;
          }

          const title = titleBySessionId.get(sessionId);

          return title && title !== tab.label
            ? { ...tab, label: title, labelKey: undefined }
            : tab;
        }),
      );

      setRecentNavTabs((tabs) =>
        tabs.map((tab) => {
          const identifiers = recentTabIdentifiersRef.current.get(tab.id);
          const sessionId =
            identifiers?.sessionId ??
            (tab.id.startsWith("chat:") ? tab.id.slice("chat:".length) : null);

          if (!sessionId) {
            return tab;
          }

          const title = titleBySessionId.get(sessionId);

          return title && title !== tab.label ? { ...tab, label: title } : tab;
        }),
      );
    },
    [setNavTabs, setRecentNavTabs],
  );

  // Refresh session titles instantly on submit activity
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const refreshNow = () => {
      void getUserSessions()
        .then(applySessionTitles)
        .catch(() => {
          // Non-fatal — titles just won't refresh this cycle.
        });
    };

    const handleChatSessionActivity = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refreshNow, 300);
    };

    window.addEventListener("kci-chat-session-activity", handleChatSessionActivity);
    return () => {
      window.removeEventListener("kci-chat-session-activity", handleChatSessionActivity);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [applySessionTitles]);

  // Direct/deep-linked navigation to the execution-stream page (e.g. typing
  // /workspace/execution-stream) lands on that page via RouteSync alone,
  // which only flips currentPage — it doesn't open a tab. Without this, the
  // page renders inside whatever tab was already active. Open/focus a
  // dedicated "Execution Stream" tab whenever that page becomes active.
  useEffect(() => {
    if (navigation.currentApp !== CONFIG.WORKSPACE || navigation.currentPage !== "__execution-stream__") {
      return;
    }

    const tabId = "execution-stream";
    if (activeNavTabIdRef.current === tabId) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs the open tab to a deep-linked route
    openOrFocusNavTab({
      id: tabId,
      label: "Execution Stream",
      removable: true,
      tabKind: "workspaces",
    });
  }, [navigation.currentApp, navigation.currentPage, openOrFocusNavTab]);

  useEffect(() => {
   

    const handleChatSessionCreated = (event: Event) => {
      const detail = (event as CustomEvent<ChatSessionCreatedDetail>).detail;
      const sessionId = detail?.sessionId?.trim();
      if (!sessionId) {
        console.warn('[KCI-ChatSession][Core] Ignoring session-created event without sessionId', detail);
        return;
      }

      // Agent preview/test sessions run inside the Create Agent panel and must not
      // open a new chat tab or navigate away from the Agent Builder.
      if (detail?.isAgentPreview === true) {
        console.info('[KCI-ChatSession][Core] Ignoring agent preview session-created event', { sessionId });
        return;
      }

      if (createdChatSessionIdsRef.current.has(sessionId)) {
        console.info('[KCI-ChatSession][Core] Ignoring duplicate session-created event', { sessionId });
        return;
      }

      createdChatSessionIdsRef.current.add(sessionId);
      const tabId = `chat:${sessionId}`;
      if (isAgentBuilderNavGuardActive(tabId)) {
        queuePendingAgentBuilderAction({
          kind: "navigation",
          execute: () => handleChatSessionCreated(event),
        });
        return;
      }
      
      const label = sanitizeTitle(detail?.name ?? '') || translateRef.current("tab.newChat");
      const agentId = detail?.agentId?.trim() || null;
      const isAgentSession = !!agentId;

      const isGlobalAgent = isAgentSession && detail.isGlobal === true;
      const isCustomAgent = isAgentSession ? detail.isCustomAgent ?? !isGlobalAgent : undefined;
      const sessionTab: NavTab = {
        id: tabId,
        label,
        removable: true,
        tabKind: "chats",
        context: {
          ...createChatTabContext(sessionId, label),
          ...(agentId ? { agentId } : {}),
        },
        isOwnedByCurrentUser: isAgentSession ? isCustomAgent === true : undefined,
        isCustomAgent,
        agentId,
        agentName: detail?.agentName?.trim() || null,
        isGlobalAgent,
      };
      const previousActiveTabId = activeNavTabIdRef.current;

      setNavTabs((prev) => {
        const existing = prev.find((tab) => tab.id === tabId);
        if (existing) {
          const next = prev.map((tab) =>
            tab.id === tabId
              ? { ...tab, label, context: sessionTab.context }
              : tab
          );
          navTabsRef.current = next;
          return next;
        }

        const next = [...prev, sessionTab];
        navTabsRef.current = next;
        return next;
      });

      activateNavTab(tabId, { trackFocus: true, skipChatSessionLoad: true });
      navMruRef.current = [
        tabId,
        ...navMruRef.current.filter((id) => id !== tabId && id !== previousActiveTabId),
      ].slice(0, MRU_HISTORY_SIZE);

      console.info('[KCI-ChatSession][Core] Registered and activated chat tab', {
        tabId,
        sessionId,
        label,
        previousActiveTabId,
      });

      // Persist the new chat tab to the backend so it appears in recent tabs.
      const panelId = tabPanelIdRef.current;
      if (panelId) {
        void saveTab(panelId, { 
          TabId: tabId, 
          Title: label, 
          Type: 'Chat', 
          SessionId: sessionId, 
          AgentId: detail?.agentId?.trim() || undefined,
          AgentName: detail?.agentName?.trim() || undefined,
          IsGlobal: detail?.agentId ? detail.isGlobal === true : null, 
          ...toBackendTabContext(sessionTab.context),
        }, { activeTabId: tabId })
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            // Backend save failure is non-fatal; recent tabs will sync on next refresh.
          });
      }

    };

      const handleChatRenameTitleUpdated = (event: Event) => {
      if (event.type !== 'kci-chat-title-updated') {
        return;
      }

      const customEvent = event as CustomEvent<{
        sessionId?: string;
        title?: string;
      }>;

      const sessionId = String(
        customEvent.detail?.sessionId ?? '',
      ).trim();

      const label = sanitizeTitle(
        String(customEvent.detail?.title ?? ''),
      );

      if (!sessionId || !label) {
        return;
      }

      const tabId = `chat:${sessionId}`;
     
      // This must happen FIRST.
      renamedChatTitlesRef.current.set(sessionId, label);

      setNavTabs((prev) => {
        const next = prev.map((tab) =>
          tab.id === tabId
            ? {
                ...tab,
                label,
                labelKey: undefined,
                context: createChatTabContext(sessionId, label),
              }
            : tab,
        );

        navTabsRef.current = next;
        return next;
      });

      setRecentNavTabs((prev) => {
        const existing = prev.find((item) =>
          isRecentEntryForSession(
            item,
            tabId,
            sessionId,
            recentTabIdentifiersRef.current,
          ),
        );

        if (!existing) {
          return prev;
        }

        const next = [
          {
            ...existing,
            label,
            timestamp: Date.now(),
          },
          ...prev.filter((item) => item.id !== existing.id),
        ].slice(0, RECENT_TABS_LIMIT);

        recentNavTabsRef.current = next;

        return next;
      });

      // Persist renamed title to backend so recent tab state survives reloads.
      const panelId = tabPanelIdRef.current;
      if (panelId) {
        void saveTab(
          panelId,
          { TabId: tabId, Title: label, Type: "Chat", SessionId: sessionId },
          { activeTabId: tabId },
        )
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            // Fall back to refresh to keep local recent tabs consistent with backend.
            void refreshRecentTabsFromBackend();
          });
      }
    };

    const handleChatTitleUpdated = (event: MessageEvent) => {
      if (event.data?.type !== 'CHAT_TITLE_UPDATED') {
        return;
      }

      const sessionId = String(event.data.sessionId ?? '').trim();
      const incomingLabel = sanitizeTitle(
        String(event.data.title ?? ''),
      );

      if (!sessionId || !incomingLabel) {
        return;
      }

      // If this session has been explicitly renamed, the renamed
      // title wins over any later/stale CHAT_TITLE_UPDATED event.
      const renamedLabel =
        renamedChatTitlesRef.current.get(sessionId);

      const label = renamedLabel ?? incomingLabel;

      const tabId = `chat:${sessionId}`;

      setNavTabs((prev) => {
        const next = prev.map((tab) =>
          tab.id === tabId
            ? {
                ...tab,
                label,
                labelKey: undefined,
                context: createChatTabContext(sessionId, label),
              }
            : tab,
        );
        navTabsRef.current = next;
        return next;
      });

      // Also update the label in any existing recent-tab entry optimistically so
      // the renamed title is immediately visible without waiting for the backend refresh.
      setRecentNavTabs((prev) => {
        const existing = prev.find((item) =>
          isRecentEntryForSession(item, tabId, sessionId, recentTabIdentifiersRef.current),
        );
        if (!existing) return prev;
        return prev.map((item) =>
          item === existing ? { ...item, label } : item,
        );
      });

      // Persist the updated title to the backend tab record, then refresh.
      const panelId = tabPanelIdRef.current;
      if (panelId) {
        void saveTab(panelId, { TabId: tabId, Title: label, Type: 'Chat', SessionId: sessionId }, { activeTabId: tabId })
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            // Fall back to a plain refresh so at least the backend state is reflected.
            void refreshRecentTabsFromBackend();
          });
      }
    };

    const handleNewChatRequested = (event: MessageEvent) => {
      if (event.data?.type === 'CHAT_NEW_REQUEST') {
        activateNavTab(NAV_FOCUS_TAB_ID, { trackFocus: true });
      }
    };

    const handleLocalNewChatRequested = () => {
      activateNavTab(NAV_FOCUS_TAB_ID, { trackFocus: true });
    };

    // Subscribe to event bus for new chat requests from Angular (clara-ai)
    const handleEventBusNewChatRequest = (event: { type: string; payload?: Record<string, unknown> }) => {
      if (event?.type === 'kci.chat.new-request') {
        activateNavTab(NAV_FOCUS_TAB_ID, { trackFocus: true });
      }
    };

    let unsubscribeEventBus: (() => void) | null = null;
    try {
      const eventBus = (window as Window & { __KCI_EVENT_BUS__?: KCiEventBusSubscriber }).__KCI_EVENT_BUS__;
      if (eventBus?.subscribe) {
        unsubscribeEventBus = eventBus.subscribe('kci.chat.new-request', handleEventBusNewChatRequest);
      }
    } catch (error) {
      console.warn('[KCI-ChatSession][Core] Failed to subscribe to event bus:', error);
    }

    const handleChatHistorySessionRenamed = (event: Event) => {
      const customEvent = event as CustomEvent<{ sessionId?: string; title?: string }>;
      const sessionId = String(customEvent.detail?.sessionId ?? '').trim();
      const incomingLabel = sanitizeTitle(String(customEvent.detail?.title ?? ''));

      if (!sessionId || !incomingLabel) {
        console.warn('[KCI-ChatHistory][Rename] Ignoring rename event with missing sessionId or title', customEvent.detail);
        return;
      }

      const tabId = `chat:${sessionId}`;
       const label = sanitizeTitle(String(customEvent.detail?.title ?? ''));


      // Record the rename so stale CHAT_TITLE_UPDATED messages don't overwrite it.
      renamedChatTitlesRef.current.set(sessionId, label);

      // Update the open nav tab title if this session is open.
      setNavTabs((prev) => {
        const matchingTab = prev.find((tab) =>
          isNavTabForSession(tab, tabId, sessionId, recentTabIdentifiersRef.current),
        );
        if (!matchingTab) return prev;
        const next = prev.map((tab) =>
          tab.id === matchingTab.id
            ? {
                ...tab,
                label,
                labelKey: undefined,
                context: createChatTabContext(sessionId, label),
              }
            : tab,
        );
        navTabsRef.current = next;
        return next;
      });

      // Update the recent tab entry title optimistically.
      setRecentNavTabs((prev) => {
        const existing = prev.find((item) =>
          isRecentEntryForSession(item, tabId, sessionId, recentTabIdentifiersRef.current),
        );
        if (!existing) return prev;
        const next = prev.map((item) =>
          item === existing ? { ...item, label, timestamp: Date.now() } : item,
        );
        recentNavTabsRef.current = next;
        return next;
      });

      // Persist the renamed title via saveTab then refresh recent tabs.
      const panelId = tabPanelIdRef.current;
      if (panelId) {
        const activeTabId = activeNavTabIdRef.current && activeNavTabIdRef.current !== NAV_FOCUS_TAB_ID
             ? activeNavTabIdRef.current
             : tabId;

        void saveTab(
          panelId,
          { TabId: tabId, Title: label, Type: 'Chat', SessionId: sessionId },
          { activeTabId: activeTabId },
        )
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            void refreshRecentTabsFromBackend();
          });
      }

      console.info('[KCI-ChatHistory][Rename] Chat session renamed', { sessionId, tabId, label });
    };

    console.info('[KCI-ChatSession][Core] Listening for kci-chat-session-created');
    window.addEventListener("kci-chat-session-created", handleChatSessionCreated);
    window.addEventListener('message', handleChatTitleUpdated);
    window.addEventListener('kci-chat-title-updated', handleChatRenameTitleUpdated); 
    window.addEventListener('message', handleNewChatRequested);
    window.addEventListener('kci-chat-new-request', handleLocalNewChatRequested);
    window.addEventListener('kci-chat-history-session-renamed', handleChatHistorySessionRenamed);
    return () => {
      console.info('[KCI-ChatSession][Core] Stopped listening for kci-chat-session-created');
      window.removeEventListener("kci-chat-session-created", handleChatSessionCreated);
      window.removeEventListener('message', handleChatTitleUpdated);
      window.removeEventListener('message', handleNewChatRequested);
      window.removeEventListener('kci-chat-new-request', handleLocalNewChatRequested);
      window.removeEventListener('kci-chat-title-updated', handleChatRenameTitleUpdated);
      window.removeEventListener('kci-chat-history-session-renamed', handleChatHistorySessionRenamed);
      if (unsubscribeEventBus) {
        unsubscribeEventBus();
      }
    };
  }, [activateNavTab, isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, refreshRecentTabsFromBackend]);

  // Create/activate "Agent Builder" tab without navigation — content already loaded in chat container.
  useEffect(() => {
    const handler = () => {
      openOrFocusNavTab({
        id: AGENT_BUILDER_TAB_ID,
        label: AGENT_BUILDER_LABEL,
        removable: true,
        tabKind: "agents",
      });
    };
    window.addEventListener('kci-navigate-to-agent-builder', handler);
    return () => window.removeEventListener('kci-navigate-to-agent-builder', handler);
  }, [openOrFocusNavTab]);


  // Auto-open / focus the correct agent tab when the agent app navigates.
  // Runs with higher priority than chat normalization to prevent unwanted focus shifts.
  useEffect(() => {
    if (!isAgentNavigationPage(navigation.currentApp, navigation.currentPage)) {
      return;
    }

    if (suppressNextAgentAutoTabRef.current) {
      suppressNextAgentAutoTabRef.current = false;
      return;
    }

    const descriptor = resolveAgentTabDescriptor(navigation.currentPage);
    const nextTab: NavTab = {
      id: descriptor.id,
      label: descriptor.label,
      removable: true,
      tabKind: "agents",
      agentId: descriptor.agentId ?? null,
      context: {
        kind: "agent",
        agentId: descriptor.agentId ?? null,
        pageId: navigation.currentPage,
        agentName: descriptor.label,
      },
    };

    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs the open tab to a deep-linked agent route
    openOrFocusNavTab(nextTab);
  }, [openOrFocusNavTab, navigation.currentApp, navigation.currentPage]);

  // handleNavAddAction
  const handleNavAddAction = useCallback(
    (action: TabAddAction) => {
      // Every branch below either switches away from the active tab or wipes
      // the tab list entirely — guard all of them up front, before any
      // mutation, so a queued "New Chat"/"New Workspace"/etc. never leaves an
      // orphan tab behind while Agent Builder's unsaved-changes dialog is pending.
      if (
        (action === "new-chat" ||
          action === "new-workspace" ||
          action === "new-agent" ||
          action === "close-all") &&
        isAgentBuilderNavGuardActive()
      ) {
        queuePendingAgentBuilderAction({ kind: "add-action", action });
        return;
      }

      if (action === "new-chat") {
        const existingBlankChat = navTabsRef.current.find(
          (tab) =>
            tab.labelKey === "nav.focus" || tab.labelKey === "tab.newChat"
        );
        if (existingBlankChat) {
          activateNavTab(existingBlankChat.id, { trackFocus: true });
          navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_CLARA_AI_CHAT, null);
          return;
        }

        navCounterRef.current += 1;
        const newId = `chat-${Date.now()}-${navCounterRef.current}`;
        const newTab: NavTab = {
          id: newId,
          label: t("tab.newChat"),
          labelKey: "tab.newChat",
          removable: true,
          tabKind: "chats",
          context: createChatTabContext(null, t("tab.newChat")),
        };
        setNavTabs((prev) => [...prev, newTab]);
        requestAnimationFrame(() => activateNavTab(newId));
        navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_CLARA_AI_CHAT, null);
        return;
      }

      if (action === "close-all") {
        setNavTabs((prev) => prev.filter((tab) => !tab.removable));
        navMruRef.current = [];
        tabDispatch({ type: "SET_COLLAPSED_GROUPS", payload: { groupIds: [] } });
        setActiveNavTabId(NAV_FOCUS_TAB_ID);
        const panelId = tabPanelIdRef.current;
        if (panelId) {
          void saveTabs(panelId, [], '')
            .then(() => void refreshRecentTabsFromBackend())
            .catch(() => {
              void refreshRecentTabsFromBackend();
            });
        }
        // Preserve recent history so users can reopen closed tabs from Recent Tabs.
        return;
      }

      if (action === "new-workspace") {
        navCounterRef.current += 1;
        const tabId = `ws-new-${Date.now()}-${navCounterRef.current}`;
        const newTab: NavTab = {
          id: tabId,
          label: t("tab.newWorkspace"),
          labelKey: "tab.newWorkspace",
          removable: true,
          clientId: "workspace-default",
          clientLabel: "AlphaGo FY28",
          engId: "workspace-default",
          role: 9,
          tabKind: "workspaces",
        };
        setNavTabs((prev) => [...prev, newTab]);
        requestAnimationFrame(() => activateNavTab(tabId));
        return;
      }

      if (action === "new-agent") {
        const newAgentTab: NavTab = {
          id: "agent:agent-builder",
          label: AGENT_BUILDER_LABEL,
          labelKey: undefined,
          removable: true,
          tabKind: "agents",
          isOwnedByCurrentUser: true,
          isCustomAgent: true,
          isPublishedCustomAgent: false,
          context: {
            kind: "agent",
            agentId: null,
            pageId: "new-agent",
            agentName: AGENT_BUILDER_LABEL,
          },
        };
        openOrFocusNavTab(newAgentTab);
      }
    },
    [activateNavTab, isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, navigateToPage, openOrFocusNavTab, refreshRecentTabsFromBackend, setActiveNavTabId, setNavTabs, t, tabDispatch]
  );

  // Global normalization: any route-level "New Chat" navigation focuses the
  // canonical New Chat tab instead of creating/landing on duplicates.
  useEffect(() => {
    if (
      navigation.currentApp === CONFIG.CLARA_APP &&
      navigation.currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT
    ) {
      // Don't override if we already have an agent tab active
      if (activeNavTabIdRef.current.startsWith("agent:")) {
        return;
      }
      if (activeNavTabIdRef.current.startsWith("chat:")) {
        return;
      }
      const activeTab = navTabsRef.current.find((tab) => tab.id === activeNavTabIdRef.current);
      if (activeTab?.tabKind === "chats") {
        return;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs the open tab to a deep-linked chat route
      handleNavAddAction("new-chat");
    }
  }, [handleNavAddAction, navigation.currentApp, navigation.currentPage]);

  // handleNavTabClose
  const handleNavTabClose = useCallback(
    (tabId: string, options?: { persist?: boolean }) => {
      const currentTabs = navTabsRef.current;
      const closingTab = currentTabs.find((tab) => tab.id === tabId);
      if (!closingTab) {
        return;
      }
      if (tabId === activeNavTabIdRef.current && isAgentBuilderNavGuardActive()) {
        queuePendingAgentBuilderAction({ kind: "close", tabId });
        return;
      }
      dirtyWizardTabIdsRef.current.delete(tabId);
      if (tabId === "agent:agent-builder") {
        const nextTabs = currentTabs.filter((tab) => tab.id !== tabId);

        navTabsRef.current = nextTabs;
        setNavTabs(nextTabs);

        navMruRef.current = navMruRef.current.filter((id) => id !== tabId);
        if (tabId !== activeNavTabIdRef.current) return;
        previousActiveNavTabIdRef.current = tabId;
        activeNavTabIdRef.current = NAV_FOCUS_TAB_ID;
        setActiveNavTabId(NAV_FOCUS_TAB_ID);
        dispatchChatSessionLoad(null);
        return;
      }

      // Objective workspace tabs keep a hidden MFE container alive in
      // BodyContent (see WorkspaceRemoteContainerStack), keyed by the
      // objective's subPage. Closing the tab must prune that container —
      // otherwise it stays mounted forever and reopening the same objective
      // just reveals the same, never-refetched instance instead of
      // remounting and re-resolving its execution.
      if (closingTab.tabKind === "workspaces") {
        getWorkspaceObjectiveCandidates(closingTab).forEach((objectiveId) => {
          window.dispatchEvent(new CustomEvent("kci:workspace-tab-closed", {
            detail: { objectiveId },
          }));
          removeWorkspaceContainerToken(
            buildWorkspaceContainerToken(CONFIG.WORKSPACE, CONFIG.FIXED_TAB_HOME, objectiveId)
          );
          markObjectiveTabClosed(objectiveId);
        });
      }

      const closingIndex = currentTabs.findIndex((tab) => tab.id === tabId);
      const nextTabs = currentTabs.filter((tab) => tab.id !== tabId);
      const nextMru = navMruRef.current
        .filter((id) => id !== tabId)
        .slice(0, MRU_HISTORY_SIZE);
      navMruRef.current = nextMru;

      let fallbackId: string | null = null;
      if (activeNavTabIdRef.current === tabId) {
        const previousActiveId = previousActiveNavTabIdRef.current;
        const mruFallback =
          nextMru.find((id) => nextTabs.some((tab) => tab.id === id)) ?? null;
        const neighborFallback =
          nextTabs[Math.max(0, Math.min(closingIndex - 1, nextTabs.length - 1))]
            ?.id ?? null;

        fallbackId =
          (previousActiveId &&
          previousActiveId !== tabId &&
          nextTabs.some((tab) => tab.id === previousActiveId)
            ? previousActiveId
            : null) ??
          mruFallback ??
          neighborFallback ??
          NAV_FOCUS_TAB_ID;

        activeNavTabIdRef.current = fallbackId;
        if (fallbackId !== NAV_FOCUS_TAB_ID) {
          previousActiveNavTabIdRef.current = tabId;
        }
      }

      navTabsRef.current = nextTabs;
      setNavTabs(nextTabs);

      // IMPORTANT: Legacy tab-patching persistence.
      // Closing a tab persists the complete remaining open-tab set and active
      // tab, then refreshes Recent Tabs from the backend.
      const panelId = tabPanelIdRef.current;
      if (panelId && options?.persist !== false) {
        const tabsToPersist = [
          ...nextTabs
          .map((tab) => toPersistedSaveTabPayload(tab, recentTabIdentifiersRef.current))
          .filter((tab): tab is SaveTabPayload => tab !== null),
          toPersistedSaveTabPayload(closingTab, recentTabIdentifiersRef.current, true),
        ].filter((tab): tab is SaveTabPayload => tab !== null);
        const activeTabForPersistence = fallbackId && fallbackId !== NAV_FOCUS_TAB_ID ? fallbackId : '';
        void saveTabs(panelId, tabsToPersist, activeTabForPersistence)
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            void refreshRecentTabsFromBackend();
          });
      }

      if (!fallbackId) {
        return;
      }

      if (fallbackId === NAV_FOCUS_TAB_ID) {
        previousActiveNavTabIdRef.current = tabId;
        // The permanent Focus tab is already the "New Chat" surface — activate it directly.
        activeNavTabIdRef.current = NAV_FOCUS_TAB_ID;
        setActiveNavTabId(NAV_FOCUS_TAB_ID);
        if (closingTab?.tabKind === 'chats') {
          dispatchChatSessionLoad(null);
          navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_CLARA_AI_CHAT, null);
          return;
        }
        else {
          navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_FOCUS);
        }
      }
      activateNavTab(fallbackId, { trackFocus: true });
    },
    [activateNavTab, isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, navigateToPage, refreshRecentTabsFromBackend, removeWorkspaceContainerToken, setActiveNavTabId, setNavTabs]
  );

  useEffect(() => {
    const handler = () => handleNavTabClose(AGENT_BUILDER_TAB_ID);
    window.addEventListener("kci-agent-builder-cancelled", handler);
    return () => window.removeEventListener("kci-agent-builder-cancelled", handler);
  }, [handleNavTabClose]);

  useEffect(() => {
    const handleDeletedChatSessions = (event: Event) => {
      const detail = (event as CustomEvent<{ sessionIds?: string[] }>).detail;
      const deletedSessionIds = new Set(
        (detail?.sessionIds ?? []).map((sessionId) => sessionId.trim()).filter(Boolean),
      );

      if (deletedSessionIds.size === 0) {
        console.warn('[KCI-ChatDelete][RecentTabs] delete event ignored: no session ids', detail);
        return;
      }

      deletedSessionIds.forEach((sessionId) => deletedChatSessionIdsRef.current.add(sessionId));
      writeStoredDeletedChatSessionIds(deletedChatSessionIdsRef.current);

      const identifierMapAtDelete = recentTabIdentifiersRef.current;
      console.info('[KCI-ChatDelete][RecentTabs] delete event received', {
        deletedSessionIdCount: deletedSessionIds.size,
        openTabCount: navTabsRef.current.length,
        recentTabCountBefore: recentNavTabsRef.current.length,
        identifierCount: identifierMapAtDelete.size,
      });

      setRecentNavTabs((prev) => {
        const next = prev.filter(
          (entry) => ![...deletedSessionIds].some((sessionId) =>
            isRecentEntryForSession(
              entry,
              `chat:${sessionId}`,
              sessionId,
              identifierMapAtDelete,
            ),
          ),
        );
        console.info('[KCI-ChatDelete][RecentTabs] local recent tabs pruned', {
          beforeCount: prev.length,
          afterCount: next.length,
          removedEntries: prev
            .filter((entry) => !next.some((nextEntry) => nextEntry.id === entry.id))
            .map((entry) => ({ id: entry.id, label: entry.label, tabKind: entry.tabKind })),
        });
        recentNavTabsRef.current = next;
        return next;
      });

      const nextIdentifierMap = new Map(identifierMapAtDelete);
      nextIdentifierMap.forEach((identifiers, entryId) => {
        const sessionId = identifiers.sessionId ?? '';
        if (
          deletedSessionIds.has(sessionId) ||
          [...deletedSessionIds].some((deletedSessionId) =>
            entryId === deletedSessionId || entryId === `chat:${deletedSessionId}`,
          )
        ) {
          nextIdentifierMap.delete(entryId);
        }
      });

      const tabIdsToClose = navTabsRef.current
        .filter((tab) => {
          if (tab.tabKind !== "chats") {
            return false;
          }

          return [...deletedSessionIds].some((sessionId) =>
            isNavTabForSession(
              tab,
              `chat:${sessionId}`,
              sessionId,
              identifierMapAtDelete,
            ),
          );
        })
        .map((tab) => tab.id);

      console.info('[KCI-ChatDelete][RecentTabs] matching open chat tabs', {
        tabIdsToClose,
      });

      recentTabIdentifiersRef.current = nextIdentifierMap;

      const activeTabId = activeNavTabIdRef.current;
      const orderedTabIds = [
        ...tabIdsToClose.filter((tabId) => tabId !== activeTabId),
        ...tabIdsToClose.filter((tabId) => tabId === activeTabId),
      ];

      orderedTabIds.forEach((tabId) => handleNavTabClose(tabId));
      if (orderedTabIds.length === 0) {
        console.info('[KCI-ChatDelete][RecentTabs] no open tab to close; waiting for next backend refresh to confirm recent-tab persistence', {
          deletedSessionIds: [...deletedSessionIds],
        });
      }
    };

    window.addEventListener("kci-chat-sessions-deleted", handleDeletedChatSessions);
    return () => {
      window.removeEventListener("kci-chat-sessions-deleted", handleDeletedChatSessions);
    };
  }, [handleNavTabClose]);

  // handleRecentNavTabOpen
  const handleRecentNavTabOpen = useCallback(
    async (entry: RecentTabEntry) => {
      const isAgentDashboard =
        entry.id === AGENT_DASHBOARD_TAB_ID ||
        entry.agentId?.toUpperCase() === AGENT_DASHBOARD_AGENT_ID;
      const identifiers = recentTabIdentifiersRef.current.get(entry.id);
      const sessionId = identifiers?.sessionId
        ?? (entry.id.startsWith("chat:")
          ? entry.id.slice("chat:".length)
          : null);
      const agentId = identifiers?.agentId ?? entry.agentId ?? null;

      const resolvedLabel =
        sessionId
          ? renamedChatTitlesRef.current.get(sessionId) ?? entry.label
          : entry.label;
      const tabKind = isAgentDashboard
        ? "agents"
        : entry.isAgentConversation
          ? "chats"
          : (entry.tabKind ?? null);

      let clientLabel = entry.clientLabel;
      const separatorIndex = entry.id.indexOf("::");
      const workspaceId =
        entry.tabKind === "workspaces" && separatorIndex > 0
          ? entry.id.slice(0, separatorIndex).trim()
          : "";
      const objectiveId =
        entry.tabKind === "workspaces" && separatorIndex > 0
          ? entry.id.slice(separatorIndex + 2).trim()
          : "";

      if (workspaceId && (!clientLabel || clientLabel === workspaceId)) {
        try {
          const { workspace } = await fetchWorkspaceObjectives(workspaceId);
          const workspaceData = workspace as Record<string, unknown> | null;
          clientLabel = String(
            workspaceData?.Name ??
            workspaceData?.name ??
            workspaceData?.workspaceName ??
            workspaceId,
          ).trim();
        } catch {
          clientLabel = workspaceId;
        }
      }

      const reopened: NavTab = {
        id: isAgentDashboard ? AGENT_DASHBOARD_TAB_ID : entry.id,
        label: resolvedLabel,
        removable: true,
        clientId: entry.groupId ?? null,
        clientLabel,
        engId: entry.engId ?? null,
        tabKind,
        context: tabKind === "chats"
          ? {
              ...createChatTabContext(sessionId, resolvedLabel),
              ...(agentId ? { agentId } : {}),
            }
          : tabKind === "workspaces" && workspaceId && objectiveId
            ? {
                kind: "workspace",
                workspaceId,
                objectiveId,
                pageId: CONFIG.FIXED_TAB_HOME,
              }
            : undefined,
        isOwnedByCurrentUser: entry.isOwnedByCurrentUser,
        isCustomAgent: entry.isCustomAgent,
        isPublishedCustomAgent: entry.isPublishedCustomAgent,
        memberFirmCode: entry.memberFirmCode ?? null,
      };

      if (entry.tabKind === "workspaces") {
        if (separatorIndex > 0) {
          if (workspaceId && objectiveId) {
            kciEventBus.publish(
              "kci.workspace.itemSelected",
              {
                tabId: reopened.id,
                workspaceId,
                objectiveId,
                objectiveType: "",
                objectiveCategory: "",
              },
              {
                source: KCI_APP_IDS.SHELL,
                target: KCI_APP_IDS.WORKSPACE_APP,
              },
            );
            markObjectiveTabOpen(objectiveId, reopened.id);
          }
        }
      }
      openOrFocusNavTab(reopened);
    },
    [openOrFocusNavTab]
  );

  useEffect(() => {
    const handleAgentChatStart = (event: Event) => {
      const customEvent = event as CustomEvent<{
        agentId?: string;
      }>;

      const agentId = customEvent.detail?.agentId?.trim().toLowerCase();
      if (!agentId) return;

      // Focus a blank chat before the agent creates a new session.
      activateNavTab(NAV_FOCUS_TAB_ID, { trackFocus: true });
    };

    window.addEventListener(
      "kci-agent-chat-start-requested",
      handleAgentChatStart,
    );
    return () =>
      window.removeEventListener(
        "kci-agent-chat-start-requested",
        handleAgentChatStart,
      );
  }, [activateNavTab]);

  // handleRecentNavTabPinToggle
  const handleRecentNavTabPinToggle = useCallback(
    (entry: RecentTabEntry, shouldPin: boolean) => {
      // Optimistic update — capture snapshot for rollback on API failure.
      setRecentNavTabs((prev) => {
        return prev.map((item) => {
          if (item.id !== entry.id) {
            return item;
          }

          return {
            ...item,
            isPinned: shouldPin,
            pinnedAt: shouldPin ? Date.now() : null,
          };
        });
      });

      const panelId = tabPanelIdRef.current;
      const isBackendKnownEntry = recentTabIdentifiersRef.current.has(entry.id);

      if (!panelId || !isBackendKnownEntry) return;

      const apiCall = shouldPin ? pinTab : unpinTab;

      apiCall(panelId, entry.id)
        .then((result) => {
          tabPanelPinsRef.current = result.Pins;
          // Refresh from backend so pin state is authoritative and consistent after reload.
          void refreshRecentTabsFromBackend();
        })
        .catch(() => {
          // Keep optimistic UI state to preserve pin/unpin UX even when sync fails.
        });
    },
    [refreshRecentTabsFromBackend, setRecentNavTabs]
  );

  const handleRecentNavTabAction = useCallback(
    async (
      entry: RecentTabEntry,
      actionId: RecentTabActionId,
      payload?: RecentTabActionPayload,
    ): Promise<RecentTabActionResult> => {
    const identifiers = recentTabIdentifiersRef.current.get(entry.id);

    const rawSessionId = identifiers?.sessionId ?? entry.id;
    const resolvedSessionId = rawSessionId.startsWith("chat:")
      ? rawSessionId.slice("chat:".length)
      : rawSessionId;

    const rawAgentId = identifiers?.agentId ?? entry.agentId ?? entry.id;
    const resolvedAgentId = rawAgentId.startsWith("agent:")
      ? rawAgentId.slice("agent:".length)
      : rawAgentId;


      if (actionId === "rename") {
        const nextLabel = sanitizeTitle(payload?.label ?? "");
        renamedChatTitlesRef.current.set(resolvedSessionId, nextLabel);
        if (!nextLabel) {
          return { status: "error" };
        }

        if (nextLabel.length > MAX_RECENT_TAB_TITLE_LENGTH) {
          return {
            status: "error",
          };
        }

        const duplicate = recentNavTabs.some(
          (item) => item.id !== entry.id && item.label.toLowerCase() === nextLabel.toLowerCase(),
        );
        if (duplicate) {
          return {
            status: "error",
          };
        }

        const openTab = navTabsRef.current.find((tab) =>
          isNavTabForSession(
            tab,
            entry.id,
            resolvedSessionId,
            recentTabIdentifiersRef.current,
          ),
        );

        const openTabIdForRename = openTab?.id ?? null;

        // Optimistic update with rollback on API failure.
        setRecentNavTabs((prev) =>
          prev.map((item) =>
            item.id === entry.id
            || item.id === resolvedSessionId
            || item.id === `chat:${resolvedSessionId}`
              ? {
                ...item,
                label: nextLabel,
              }
              : item,
          ),
        );

        setNavTabs((prev) => {
          const next = prev.map((tab) =>
            tab.id === (openTabIdForRename ?? entry.id)
              ? {
                ...tab,
                label: nextLabel,
                labelKey: undefined,
                context: createChatTabContext(resolvedSessionId, nextLabel),
              }
              : tab,
          );
          navTabsRef.current = next;
          return next;
        });

        // This prevents the old title from being rendered while the rename API completes.
        window.dispatchEvent(
          new CustomEvent("kci-chat-title-updated", {
            detail: {
              sessionId: resolvedSessionId,
              title: nextLabel,
              source: "rename",
            },
          }),
        );

        try {
          await renameSession(resolvedSessionId, nextLabel);
          renamedChatTitlesRef.current.set(resolvedSessionId, nextLabel);
          chatTitleBySessionIdRef.current.set(resolvedSessionId, nextLabel);
          return {
            status: "success",
          };
        } catch (error) {
          return {
            status: "error",
            message: normalizeActionError("rename", error),
          };
        }
      }

      if (actionId === "share") {
        if (entry.label === AGENT_DASHBOARD_LABEL) {
          return {
            status: "error",
          };
        }

        const recipients = payload?.recipients ?? [];
        if (recipients.length === 0) {
          return {
            status: "error",
          };
        }

        try {
          await shareAgent(resolvedAgentId, recipients);
          return {
            status: "success",
          };
        } catch (error) {
          return {
            status: "error",
            message: normalizeActionError("share", error),
          };
        }
      }

      if (actionId === "download") {
        if (entry.tabKind !== "chats" && entry.tabKind !== "workspaces") {
          return {
            status: "error",
          };
        }

        if (payload?.format && payload.format !== "xlsx") {
          return { status: "error" };
        }

        try {
          const blob = await downloadChat(resolvedSessionId);
          const requestedName = payload?.fileName?.trim() ?? "";
          const resolvedName = sanitizeDownloadFileName(requestedName || entry.label || "Chat");
          const fileName = resolvedName.toLowerCase().endsWith(".xlsx")
            ? resolvedName
            : `${resolvedName}.xlsx`;
          const objectUrl = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = fileName;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(objectUrl);

          return {
            status: "success",
          };
        } catch (error) {
          return {
            status: "error",
            message: normalizeActionError("download", error),
          };
        }
      }

      if (actionId === "edit") {
        if ((entry.tabKind === "agents" || entry.agentId) && entry.isOwnedByCurrentUser === false) {
          return {
            status: "error",
          };
        }

        if (entry.tabKind === "agents" || entry.agentId) {
          let agentDetails: unknown;
          try {
            agentDetails = await getAgentDetails(resolvedAgentId);
          } catch (error) {
            return {
              status: "error",
              message: normalizeActionError("edit", error),
            };
          }

          const tabId = "agent:agent-builder";
          const editRequest = {
            agentId: resolvedAgentId,
            tabId,
            label: AGENT_BUILDER_LABEL,
            agentDetails,
          };

          const executeEdit = () => {
            try {
              window.sessionStorage.setItem(
                "kci.pendingRecentTabsAgentEdit",
                JSON.stringify(editRequest),
              );
            } catch {
              // The live event still handles an already-mounted remote.
            }

            window.dispatchEvent(new CustomEvent("kci-navigate-to-agent-builder"));

            window.setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("kci:editAgentFromRecentTabs", {
                  detail: editRequest,
                }),
              );
            }, 0);
          };

          if (isAgentBuilderNavGuardActive()) {
            queuePendingAgentBuilderAction({ kind: "navigation", execute: executeEdit });
            return { status: "success" };
          }

          executeEdit();
          return { status: "success" };
        }

        return { status: "error" };
      }

      if (actionId === "delete") {
        if ((entry.tabKind === "agents" || entry.agentId) && entry.isOwnedByCurrentUser === false) {
          return {
            status: "error",
          };
        }

        const openTabIdToClose = resolveOpenTabIdForEntry(entry, navTabsRef.current);
        const isAgentEntry = entry.tabKind === "agents";

        try {
          if (isAgentEntry) {
            const sessionId = identifiers?.sessionId
              ?? (entry.id.startsWith("chat:") ? resolvedSessionId : null);
            await Promise.allSettled([
              ...(sessionId ? [deleteSession(sessionId)] : []),
              deleteAgentRuns(resolvedAgentId),
              deleteAgent(resolvedAgentId),
            ]);
          } else {
            await deleteSession(resolvedSessionId);
          }

          setRecentNavTabs((prev) => {
            const next = prev.filter(
              (item) =>
                item.id !== entry.id
                && (!openTabIdToClose || item.id !== openTabIdToClose),
            );
            recentNavTabsRef.current = next;
            return next;
          });
          recentTabIdentifiersRef.current.delete(entry.id);

          if (openTabIdToClose) {
            handleNavTabClose(openTabIdToClose, { persist: false });
          }

          const panelId = tabPanelIdRef.current;
          if (panelId) {
            await removeTab(panelId, identifiers?.tabId ?? entry.id, {
              sessionId: identifiers?.sessionId,
              agentId: isAgentEntry ? resolvedAgentId : null,
            }).catch(() => undefined);
          }

          if (isAgentEntry) {
            window.dispatchEvent(new CustomEvent("kci-agent-deleted", {
              detail: { agentId: resolvedAgentId },
            }));
          }

          return {
            status: "success",
          };
        } catch (error) {
          return {
            status: "error",
            message: normalizeActionError("delete", error),
          };
        }
      }

      return { status: "error" };
    },
    [
      activateNavTab,
      handleNavTabClose,
      isAgentBuilderNavGuardActive,
      navigateToPage,
      openOrFocusNavTab,
      queuePendingAgentBuilderAction,
      recentNavTabs,
      setNavTabs,
      setRecentNavTabs,
    ],
  );

  // handleWorkspaceSecondaryTabOpen (DockFlyout integration)
  const handleWorkspaceSecondaryTabOpen: NonNullable<
    UseDockFlyoutOptions["onWorkspaceSecondaryItemSelect"]
  > = useCallback(
    (payload) => {
      const groupId = payload.workspace.id;
      const groupLabel = payload.workspace.name;
      const tabId = `${groupId}::${payload.child.id}`;
      const workspaceId = (payload.child.filters?.workspaceId as string | undefined)?.trim() || groupId;
      const objectiveId = (payload.child.filters?.objectiveId as string | undefined)?.trim() || payload.child.id;
      const objectiveType = String(payload.child.filters?.objectiveType ?? '').trim();
      const objectiveCategory = String(payload.child.filters?.objectiveCategory ?? '').trim();

      const nextTab: NavTab = {
        id: tabId,
        label: payload.child.name,
        removable: true,
        clientId: groupId,
        clientLabel: groupLabel,
        engId: tabId,
        role: 9,
        tabKind: "workspaces",
        context: {
          kind: "workspace",
          workspaceId,
          objectiveId,
          objectiveType,
          objectiveCategory,
          pageId: CONFIG.FIXED_TAB_HOME,
        },
      };

      openOrFocusNavTab(nextTab);
      markObjectiveTabOpen(objectiveId, tabId);
      // Navigate to __home__ so BodyContent renders RemoteWorkspaceDashboard
      // (kci-workspace MFE on port 4400) for the selected workspace item.

      kciEventBus.publish(
          "kci.workspace.itemSelected",
          { tabId, workspaceId, objectiveId, objectiveType, objectiveCategory },
          { source: KCI_APP_IDS.SHELL, target: KCI_APP_IDS.WORKSPACE_APP },
        );

      // Persist the workspace tab with its objectiveId to the backend so it
      // appears in recent tabs and can be restored on next load.
      const panelId = tabPanelIdRef.current;
      if (panelId) {
        void saveTab(
          panelId,
          {
            TabId: tabId,
            Title: payload.child.name,
            Type: 'Workspace',
            WorkspaceName: groupLabel,
            WorkspaceObjectiveId: objectiveId,
            ...toBackendTabContext(nextTab.context),
          },
          { activeTabId: tabId },
        )
          .then(() => void refreshRecentTabsFromBackend())
          .catch(() => {
            // Non-fatal — recent tabs will reflect state on next refresh.
          });
      }

      navigateToPage(CONFIG.WORKSPACE, CONFIG.FIXED_TAB_HOME, objectiveId);
      return true;
    },
    [navigateToPage, openOrFocusNavTab, refreshRecentTabsFromBackend]
  );

  // handleChatItemTabOpen (DockFlyout integration)
  const handleChatItemTabOpen: NonNullable<
    UseDockFlyoutOptions["onChatItemSelect"]
  > = useCallback(
    (item) => {
      const sessionId = item.id.trim();
      if (!sessionId) {
        return false;
      }

      const sourceEntry = recentNavTabsRef.current.find((entry) =>
        isRecentEntryForSession(
          entry,
          `chat:${sessionId}`,
          sessionId,
          recentTabIdentifiersRef.current,
        ),
      );

      const nextTab: NavTab = {
        id: `chat:${sessionId}`,
        label: item.name?.trim() || t("tab.newChat"),
        removable: true,
        tabKind: "chats",
        context: createChatTabContext(
          sessionId,
          item.name?.trim() || t("tab.newChat"),
        ),
        isOwnedByCurrentUser: sourceEntry?.isOwnedByCurrentUser,
        isCustomAgent: sourceEntry?.isCustomAgent,
        isPublishedCustomAgent: sourceEntry?.isPublishedCustomAgent,
        memberFirmCode: sourceEntry?.memberFirmCode ?? null,
        agentId: sourceEntry?.agentId ?? null,
        agentName: sourceEntry?.agentName ?? null,
        isGlobalAgent: sourceEntry?.isGlobal === true,
      };
      console.log(nextTab)
      openOrFocusNavTab(nextTab);
      navigateToPage(CONFIG.CLARA_APP, CONFIG.FIXED_TAB_CLARA_AI_CHAT, sessionId);
      return true;
    },
    [navigateToPage, openOrFocusNavTab, t]
  );

  const handleAgentItemTabOpen: NonNullable<
    UseDockFlyoutOptions["onAgentItemSelect"]
  > = useCallback(
    (item) => {
      const pageId = item.pageId?.trim() || "";
      const agentId = resolveAgentIdFromPageId(pageId);
      const label = sanitizeTitle(item.name || "") || toTitleCaseFromSlug(pageId);

      if (!agentId || !label) {
        console.warn('[KCI-RecentTabs][Agent] unable to resolve selected agent metadata', {
          itemId: item.id,
          pageId,
          name: item.name,
        });
        return false;
      }

      const isDashboard = agentId === AGENT_DASHBOARD_AGENT_ID;
      openOrFocusNavTab({
        id: isDashboard
          ? AGENT_DASHBOARD_TAB_ID
          : `agent:${toCanonicalAgentKey(label.replace(/\s+activity$/i, "").trim())}`,
        label: isDashboard ? AGENT_DASHBOARD_LABEL : label,
        removable: true,
        tabKind: "agents",
        agentId,
        context: {
          kind: "agent",
          agentId,
          pageId,
          agentName: isDashboard ? AGENT_DASHBOARD_LABEL : label,
        },
      });
      return true;
    },
    [openOrFocusNavTab],
  );

  const handleDashboardNewChatAction: NonNullable<
    UseDockFlyoutOptions["onNewChatActionSelect"]
  > = useCallback(() => {
    handleNavAddAction("new-chat");
    return true;
  }, [handleNavAddAction]);

  const handleDashboardNewAgentAction: NonNullable<
    UseDockFlyoutOptions["onNewAgentActionSelect"]
  > = useCallback(() => {
    handleNavAddAction("new-agent");
    return true;
  }, [handleNavAddAction]);

  // onToggleGroupCollapse
  const onToggleGroupCollapse = useCallback((groupId: string) => {
    tabDispatch({ type: "TOGGLE_GROUP_COLLAPSE", payload: { groupId } });
  }, [tabDispatch]);

  // handleOpenReviewBalanceTab — opens a dedicated "Review Balance" workspace tab.
  // Triggered by the kci:openReviewBalance CustomEvent dispatched from kci-workspace
  // when the user clicks the "Review Balance" button in Configuration and Setup.
  const handleOpenReviewBalanceTab = useCallback(
    (storageKey: string, datasetLabel: string) => {
      const tabId = `review-balance:${storageKey}`;
      const sourceTab = navTabsRef.current.find(
        (tab) => tab.id === activeNavTabIdRef.current,
      );

      const nextTab: NavTab = {
        id: tabId,
        label: 'Review Balance',
        removable: true,
        clientId: sourceTab?.clientId,
        clientLabel: sourceTab?.clientLabel ?? datasetLabel,
        engId: sourceTab?.engId,
        role: sourceTab?.role,
        tabKind: 'workspaces',
      }

      // openOrFocusNavTab calls activateNavTab, which now detects the
      // review-balance: prefix and navigates to __review-balance__ instead of __home__.
      openOrFocusNavTab(nextTab)
    },
    [openOrFocusNavTab],
  )

  const handleOpenInTab = useCallback(
    (detail: { contentType: string; storageKey: string; tabLabel: string }) => {
      const tabId = `${detail.contentType}:${detail.storageKey}`
      console.info('[useNavTabs] Opening in tab', { ...detail, tabId })

      if (detail.contentType === 'view-report') {
        const existingReportTab = navTabsRef.current.find((tab) => tab.id.startsWith('view-report:'))
        if (existingReportTab && existingReportTab.id !== tabId) {
          const next = navTabsRef.current.map((tab) =>
            tab.id === existingReportTab.id
              ? { ...tab, id: tabId, label: detail.tabLabel }
              : tab,
          )
          navTabsRef.current = next
          setNavTabs(next)
          activateNavTab(tabId, { trackFocus: true })
          return
        }
      }
     
      const sourceTab = navTabsRef.current.find(
        (tab) => tab.id === activeNavTabIdRef.current,
      );

      const nextTab: NavTab = {
        id: tabId,
        label: detail.tabLabel,
        removable: true,
        tabKind: 'workspaces',
        clientId: sourceTab?.clientId,
        clientLabel: sourceTab?.clientLabel,
        engId: sourceTab?.engId,
        role: sourceTab?.role,
      }
      openOrFocusNavTab(nextTab)
    },
    [activateNavTab, openOrFocusNavTab, setNavTabs],
  )

  // Resolves the queued action (if any) once the user confirms leaving
  // the unsaved form. Clearing the dirty flags first lets the
  // resolved action pass straight through each function's own guard check.
  const confirmAgentBuilderNavAction = useCallback(() => {
    const pending = pendingAgentBuilderNavAction;
    isAgentBuilderDirtyRef.current = false;
    dirtyWizardTabIdsRef.current.delete(activeNavTabIdRef.current);
    setPendingAgentBuilderNavAction(null);
    if (!pending) return;
    if (pending.kind === "activate") {
      activateNavTab(pending.tabId, pending.options);
    } else if (pending.kind === "close") {
      handleNavTabClose(pending.tabId);
    } else if (pending.kind === "open") {
      openOrFocusNavTab(pending.nextTab);
    } else if (pending.kind === "add-action") {
      handleNavAddAction(pending.action);
    } else if (pending.kind === "dock-navigation" || pending.kind === "navigation") {
      pending.execute();
    }
  }, [pendingAgentBuilderNavAction, activateNavTab, handleNavTabClose, openOrFocusNavTab, handleNavAddAction]);

  const cancelAgentBuilderNavAction = useCallback(() => {
    setPendingAgentBuilderNavAction(null);
  }, []);

  return {
    navTabs,
    activeNavTabId,
    activeNavTab,
    activeNavTabContext,
    recentNavTabs,
    collapsedGroupIds,
    activateNavTab,
    handleNavTabClose,
    handleNavAddAction,
    handleRecentNavTabOpen,
    handleRecentNavTabPinToggle,
    handleRecentNavTabAction,
    handleWorkspaceSecondaryTabOpen,
    handleChatItemTabOpen,
    handleAgentItemTabOpen,
    handleDashboardNewChatAction,
    handleDashboardNewAgentAction,
    onToggleGroupCollapse,
    openOrFocusNavTab,
    handleOpenReviewBalanceTab,
    handleOpenInTab,
    isAgentBuilderDirtyRef,
    pendingAgentBuilderNavAction,
    confirmAgentBuilderNavAction,
    cancelAgentBuilderNavAction,
    isAgentBuilderNavGuardActive,
    queuePendingAgentBuilderAction,
  };
}


function createRecentTabEntryFromNavTab(tab: NavTab): RecentTabEntry {
  return {
    id: tab.id,
    label: tab.label,
    clientLabel: tab.clientLabel,
    groupId: tab.clientId ?? null,
    engId: tab.engId,
    timestamp: Date.now(),
    tabKind: tab.tabKind ?? null,
    isOwnedByCurrentUser: tab.isOwnedByCurrentUser,
    isCustomAgent: tab.isCustomAgent,
    isPublishedCustomAgent: tab.isPublishedCustomAgent,
    agentId: tab.agentId ?? null,
    agentName: tab.agentName ?? null,
    isGlobal: tab.isGlobalAgent ?? false,
    isAgentConversation: !!tab.agentId,
    memberFirmCode: tab.memberFirmCode ?? null,
  };
}